import { useResolvedRateSchedules } from "../../../hooks/useRates";
import { currentSchedules } from "../../../utils/rateSets";
import { isStandardPepmCoverageType } from "../../../utils/rate-helpers";

import { AdndRateRenderer } from "./AdndRateRenderer";
import { AgeBandedGenderRenderer } from "./AgeBandedGenderRenderer";
import { AgeBandedRenderer } from "./AgeBandedRenderer";
import { AgeBandedTieredRenderer } from "./AgeBandedTieredRenderer";
import { AgeBandedTobaccoRenderer } from "./AgeBandedTobaccoRenderer";
import { CompositeRenderer } from "./CompositeRenderer";
import { DependentRateRenderer } from "./DependentRateRenderer";
import { FlatRateRenderer } from "./FlatRateRenderer";
import { FmlaPepmRenderer } from "./FmlaPepmRenderer";
import { StandardPepmRenderer } from "./StandardPepmRenderer";

import type { AxiosInstance } from "axios";
import type { FC, ReactNode } from "react";
import type { RateSchedule } from "../../../types";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";

export interface RatePlanRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type (e.g., dental, vision, std) for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  /** When true, renders rate inputs as read-only (e.g. the Incumbent/Requested column). */
  readOnly?: boolean;
  /** The benefit plan group this plan belongs to, used for cross-plan rate copy */
  benefitPlanGroup?: BenefitPlanGroup;
  /** All plans in the coverage offered as age-banded copy destinations */
  copyDestinationPlans?: BenefitPlan[];
}

// Coverage types that can have AD&D secondary schedules
const ADND_COVERAGE_TYPES = ["basiclifeadnd", "voluntarylifeadnd"];

/** Check if coverage supports AD&D */
const isLifeCoverageType = (coverageType?: string): boolean =>
  ADND_COVERAGE_TYPES.includes(coverageType?.toLowerCase() ?? "");

/**
 * Picks the primary (non-AD&D) schedule for life products and reports
 * whether a secondary AD&D schedule is also present. Non-life products have
 * no AD&D concept, so the first schedule is always primary.
 */
const resolvePrimaryAndAdndSchedule = (
  rateSchedules: RateSchedule[] | undefined,
  isLife: boolean,
): { rateSchedule?: RateSchedule; hasAdndSchedule: boolean } => {
  if (!isLife) {
    return { rateSchedule: rateSchedules?.[0], hasAdndSchedule: false };
  }

  return {
    rateSchedule:
      rateSchedules?.find((s) => s.rateCoverageIndicator !== "adnd") ??
      rateSchedules?.[0],
    hasAdndSchedule:
      rateSchedules?.some((s) => s.rateCoverageIndicator === "adnd") ?? false,
  };
};

/**
 * Renders a single benefit plan's rate schedule based on its rate design.
 * For life products, also renders the AD&D rate schedule below the primary.
 * Routes to the appropriate renderer based on rate design type.
 */
export const RatePlanRenderer: FC<RatePlanRendererProps> = ({
  plan,
  axiosInstance,
  coverageType,
  onRateSaved,
  readOnly,
  benefitPlanGroup,
  copyDestinationPlans,
}) => {
  // Route using the same live-cache-aware source every leaf renderer already
  // reads from (useResolvedRateSchedules), falling back to `plan.rateSchedule`
  // (the embedded snapshot) only while the cache hasn't resolved yet — e.g. a
  // BTX plan's rateSchedule is embedded synchronously with no query needed.
  // Rate saves write straight into the cache without forcing a refetch (to
  // avoid remounting the whole grid), so relying on the prop alone would mean
  // a plan going from "no schedule" to a freshly-created one never routes to
  // a renderer at all until some unrelated refetch happened to land.
  const { data: resolvedRateSchedules } = useResolvedRateSchedules(
    plan,
    axiosInstance,
  );
  const rateSchedules = resolvedRateSchedules ?? plan.rateSchedule;

  const isLife = isLifeCoverageType(coverageType);
  // Route on the current-category schedules only; renewal/revised sets are
  // additional schedules on the same plan and must not drive design/AD&D routing.
  const { rateSchedule, hasAdndSchedule } = resolvePrimaryAndAdndSchedule(
    currentSchedules(rateSchedules),
    isLife,
  );

  if (!rateSchedule) {
    return null;
  }

  const rateDesign = rateSchedule.rateDesign;

  /** Wraps children with dependent rates + AD&D sections for flat rate plans */
  const withDependentAndAdnd = (children: ReactNode): ReactNode => {
    // Dependent rates are always shown for flat rate designs
    const hasDependentRates = rateDesign === "flatRate";

    return (
      <>
        {children}
        {hasDependentRates && (
          <DependentRateRenderer
            plan={plan}
            axiosInstance={axiosInstance}
            coverageType={coverageType}
            onRateSaved={onRateSaved}
            readOnly={readOnly}
          />
        )}
        {hasAdndSchedule && (
          <AdndRateRenderer
            plan={plan}
            axiosInstance={axiosInstance}
            coverageType={coverageType}
            onRateSaved={onRateSaved}
            readOnly={readOnly}
          />
        )}
      </>
    );
  };

  /** Wraps children with AD&D section only (non-flat designs) */
  const withAdnd = (children: ReactNode): ReactNode => {
    if (!hasAdndSchedule) return children;
    return (
      <>
        {children}
        <AdndRateRenderer
          plan={plan}
          axiosInstance={axiosInstance}
          coverageType={coverageType}
          onRateSaved={onRateSaved}
          readOnly={readOnly}
        />
      </>
    );
  };

  // Route to specific renderer based on rate design
  switch (rateDesign) {
    case "flatRate": {
      const rates = rateSchedule.rates || [];
      const hasFmlaPepm = rates.some((r) => r.unit === "fmlaPepm");

      // Both FMLA and Standard PEPM now have all 3 rates, distinguish by coverage type
      if (hasFmlaPepm) {
        const coverageLower = coverageType?.toLowerCase() || "";

        // Standard PEPM coverages: show only totalPepm for editing
        if (isStandardPepmCoverageType(coverageLower)) {
          return withAdnd(
            <StandardPepmRenderer
              plan={plan}
              axiosInstance={axiosInstance}
              coverageType={coverageType}
              onRateSaved={onRateSaved}
              readOnly={readOnly}
            />,
          );
        }

        // FMLA: show all 4 rows (W2 Employees, FMLA PEPM, ADA PEPM, TOTAL PEPM)
        return withAdnd(
          <FmlaPepmRenderer
            plan={plan}
            axiosInstance={axiosInstance}
            coverageType={coverageType}
            onRateSaved={onRateSaved}
            readOnly={readOnly}
          />,
        );
      }

      return withDependentAndAdnd(
        <FlatRateRenderer
          plan={plan}
          axiosInstance={axiosInstance}
          coverageType={coverageType}
          onRateSaved={onRateSaved}
          readOnly={readOnly}
        />,
      );
    }

    case "composite":
      return withAdnd(
        <CompositeRenderer
          plan={plan}
          axiosInstance={axiosInstance}
          coverageType={coverageType}
          onRateSaved={onRateSaved}
          readOnly={readOnly}
        />,
      );

    case "ageBanded":
      return withAdnd(
        <AgeBandedRenderer
          plan={plan}
          axiosInstance={axiosInstance}
          coverageType={coverageType}
          onRateSaved={onRateSaved}
          readOnly={readOnly}
          benefitPlanGroup={benefitPlanGroup}
          copyDestinationPlans={copyDestinationPlans}
        />,
      );

    case "ageBandedTobacco":
      return withAdnd(
        <AgeBandedTobaccoRenderer
          plan={plan}
          axiosInstance={axiosInstance}
          coverageType={coverageType}
          onRateSaved={onRateSaved}
          readOnly={readOnly}
          benefitPlanGroup={benefitPlanGroup}
          copyDestinationPlans={copyDestinationPlans}
        />,
      );

    case "ageBandedGender":
      return withAdnd(
        <AgeBandedGenderRenderer
          plan={plan}
          axiosInstance={axiosInstance}
          coverageType={coverageType}
          onRateSaved={onRateSaved}
          readOnly={readOnly}
          benefitPlanGroup={benefitPlanGroup}
          copyDestinationPlans={copyDestinationPlans}
        />,
      );

    case "ageBandedTiered":
      return withAdnd(
        <AgeBandedTieredRenderer
          plan={plan}
          axiosInstance={axiosInstance}
          coverageType={coverageType}
          onRateSaved={onRateSaved}
          readOnly={readOnly}
          benefitPlanGroup={benefitPlanGroup}
          copyDestinationPlans={copyDestinationPlans}
        />,
      );

    default:
      // Fallback: show JSON for unimplemented designs
      return withAdnd(
        <div style={{ padding: "4px 8px" }}>
          <div
            style={{ fontSize: "10px", fontWeight: 600, marginBottom: "4px" }}
          >
            {rateDesign} (not implemented)
          </div>
          <pre style={{ fontFamily: "monospace", fontSize: "11px", margin: 0 }}>
            {JSON.stringify(rateSchedule, null, 2)}
          </pre>
        </div>,
      );
  }
};
